import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Search, FileText, ArrowRight, ChevronLeft, ChevronRight, Building2, BadgeCheck, Printer, ArrowDownAZ, ArrowUpAZ } from "lucide-react";
import { categoryTint } from "@/lib/categoryColors";
import { useI18n } from "@/context/I18nContext";

export default function FormsCatalog({ sosSection = false }) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("asc");
  const active = params.get("category") || "All";
  const source = sosSection || params.get("source") === "sos" ? "sos" : "court";

  useEffect(() => { api.get("/forms/categories").then(({ data }) => setCats(data.categories)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [active, debouncedQ, source, sort]);

  useEffect(() => {
    setLoading(true);
    const query = { page, page_size: 24, sort };
    if (active !== "All") query.category = active;
    if (debouncedQ) query.q = debouncedQ;
    if (source) query.source = source;
    api.get("/forms", { params: query }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [active, debouncedQ, page, source, sort]);

  const setCat = (c) => { const p = new URLSearchParams(params); if (c === "All") p.delete("category"); else p.set("category", c); setParams(p); };
  const isSos = source === "sos";
  const shownCats = isSos ? cats.filter((c) => (c.name || "").startsWith("SOS ·")) : cats.filter((c) => !(c.name || "").startsWith("SOS ·"));
  const totalAll = shownCats.reduce((a, c) => a + c.count, 0);

  return (
    <div>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-8">
        <p className="overline text-accent mb-3">{isSos ? "NC Secretary of State · Free & fillable" : t("catalog.eyebrow", { count: totalAll || "1,100" })}</p>
        <h1 className="font-serif font-bold text-5xl sm:text-6xl tracking-tighter mb-6">{isSos ? "Secretary of State forms" : t("catalog.title")}</h1>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 max-w-3xl">
          <div className="relative flex-1 max-w-lg">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="forms-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("catalog.searchPlaceholder")} className="rounded-sm h-12 pl-10" />
          </div>
          <Button type="button" variant="outline" data-testid="sort-toggle-btn"
            onClick={() => setSort((s) => (s === "asc" ? "desc" : "asc"))}
            className="rounded-sm h-12 gap-2 shrink-0 hover:bg-secondary">
            {sort === "asc" ? <ArrowDownAZ size={18} /> : <ArrowUpAZ size={18} />}
            Form # {sort === "asc" ? "A–Z" : "Z–A"}
          </Button>
        </div>
      </section>

      {isSos && (
        <section className="max-w-7xl mx-auto px-6 pb-4" data-testid="sos-intro-banner">
          <div className="bg-primary text-primary-foreground border-[3px] border-black p-8 lg:p-10">
            <div className="flex items-start gap-4 mb-6">
              <span className="grid place-items-center w-12 h-12 bg-accent text-accent-foreground shrink-0"><Building2 size={24} /></span>
              <div>
                <h2 className="font-serif font-bold text-2xl lg:text-3xl tracking-tight leading-tight">Why file with the NC Secretary of State?</h2>
                <p className="text-primary-foreground/80 mt-2 leading-relaxed max-w-3xl">
                  The North Carolina Secretary of State is the state agency for registering and maintaining businesses, nonprofits and charities, notaries, trademarks, lobbyists, and UCC filings. Every form here is <span className="text-white font-medium">official, free to complete, and print-ready</span> — no subscription required.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: BadgeCheck, t: "Free & official", d: "Straight from sosnc.gov — always the current version, no paywall." },
                { icon: FileText, t: "Fill online", d: "Type directly onto fillable forms; flat forms link to the SOS online portal." },
                { icon: Printer, t: "Print & submit", d: "Download a clean, court-ready copy to mail or file with the state." },
              ].map((f, i) => (
                <div key={i} className="border-2 border-white/20 p-4">
                  <f.icon size={18} className="text-accent mb-2" />
                  <p className="font-medium text-sm">{f.t}</p>
                  <p className="text-xs text-primary-foreground/70 mt-1 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 pb-24 grid lg:grid-cols-12 gap-10">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <p className="overline font-bold text-muted-foreground mb-4">{t("catalog.categories")}</p>
          <div className="flex lg:flex-col gap-1.5 flex-wrap">
            {[{ name: "All", count: totalAll }, ...shownCats].map((c) => (
              <button key={c.name} data-testid={`cat-filter-${c.name}`} onClick={() => setCat(c.name)}
                style={{ backgroundColor: categoryTint(c.name) }}
                className={`flex items-center justify-between gap-2 px-3 py-1.5 text-[0.8rem] leading-tight border-2 border-black text-left lg:w-full text-black transition-transform hover:-translate-y-0.5 ${active === c.name ? "font-bold ring-2 ring-black ring-offset-1" : "font-medium"}`}>
                <span className="truncate">{c.name === "All" ? t("catalog.all") : c.name}</span><span className="font-mono text-xs shrink-0">{c.count}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-9">
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-muted-foreground">{t("catalog.results", { count: data.total })} · <span className="font-medium text-foreground">{active === "All" ? t("catalog.all") : active}</span></p>
            {data.pages > 1 && <p className="text-sm text-muted-foreground">{t("catalog.pageOf", { page: data.page, pages: data.pages })}</p>}
          </div>
          {loading ? (
            <p className="text-muted-foreground">{t("catalog.loading")}</p>
          ) : data.items.length === 0 ? (
            <p className="text-muted-foreground">{t("catalog.noMatch")}</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                {data.items.map((f) => (
                  <Link key={f.number} to={`/forms/${f.number}`} data-testid={`form-card-${f.number}`}
                    className="grid-card p-6 flex flex-col group">
                    <div className="flex items-center justify-between mb-4">
                      <Badge variant="outline" className="rounded-sm font-mono text-xs">{f.number}</Badge>
                      <FileText size={18} className="text-muted-foreground group-hover:text-accent transition-colors" />
                    </div>
                    <h3 className="font-serif font-bold text-xl leading-tight mb-2">{f.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 flex-1">{f.description}</p>
                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
                      <span className="text-xs text-muted-foreground uppercase tracking-wider truncate">{f.category}</span>
                      <span className="flex items-center gap-1 text-sm text-accent font-medium shrink-0">{t("catalog.open")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </Link>
                ))}
              </div>

              {data.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <Button variant="outline" size="sm" disabled={page <= 1} data-testid="prev-page-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-sm gap-1"><ChevronLeft size={16} /> {t("catalog.prev")}</Button>
                  <span className="text-sm text-muted-foreground px-3">{data.page} / {data.pages}</span>
                  <Button variant="outline" size="sm" disabled={page >= data.pages} data-testid="next-page-btn"
                    onClick={() => setPage((p) => Math.min(data.pages, p + 1))} className="rounded-sm gap-1">{t("catalog.next")} <ChevronRight size={16} /></Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
